// Constants
const ELEMENT_NAME = 'quantity-input';
const EVENT_TYPES = {
    CHANGE: 'change',
    CLICK: 'click',
    KEYDOWN: 'keydown'
};
const KEYS = {
    ENTER: 'Enter'
};
const BUTTON_NAMES = {
    PLUS: 'plus',
    MINUS: 'minus'
};
const DEBOUNCE_DELAY = 500; // 500ms delay

class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input');
        
        // Defensive check to ensure input exists
        if (!this.input) {
            console.warn('QuantityInput: No input element found');
            return;
        }
        
        this.currentQty = this.input.value;
        this.changeEvent = new Event(EVENT_TYPES.CHANGE, { bubbles: true });
        this.debounceTimer = null;

        this.addEventListener(EVENT_TYPES.CLICK, this.handleClick.bind(this));
        this.input.addEventListener(EVENT_TYPES.KEYDOWN, this.handleKeydown.bind(this));
        this.input.addEventListener('change', this.handleInputChange.bind(this));
    }

    /**
     * Handles 'click' events on the quantity input element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
        if (!evt.target.matches('button') || !this.input) return;
        evt.preventDefault();

        if (evt.target.name === BUTTON_NAMES.PLUS) {
            this.input.stepUp();
        } else if (evt.target.name === BUTTON_NAMES.MINUS) {
            this.input.stepDown();
        }

        if (this.input.value !== this.currentQty) {
            this.debouncedChange();
        }
    }

    /**
     * Handles direct input changes (e.g., typing)
     * @param {object} evt - Event object.
     */
    handleInputChange(evt) {
        // Don't prevent default for direct input changes
        if (this.input.value !== this.currentQty) {
            this.debouncedChange();
        }
    }

    /**
     * Debounced change handler to prevent rapid API calls
     */
    debouncedChange() {
        // Clear any existing timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        // Set a new timer
        this.debounceTimer = setTimeout(() => {
            // Only dispatch change event to avoid duplicate cart updates
            const changeEvent = new Event('change', { bubbles: true });
            this.input.dispatchEvent(changeEvent);
            
            this.currentQty = this.input.value;
        }, DEBOUNCE_DELAY);
    }

    /**
     * Handles 'keydown' events on the input field.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
        if (evt.key !== KEYS.ENTER || !this.input) return;
        evt.preventDefault();

        if (this.input.value !== this.currentQty) {
            // For Enter key, update immediately with only change event
            const changeEvent = new Event('change', { bubbles: true });
            this.input.dispatchEvent(changeEvent);
            
            this.currentQty = this.input.value;
            this.input.blur();
        }
    }
}

customElements.define(ELEMENT_NAME, QuantityInput);
